"use client";

import { useLang } from "@/contexts/language-context";
import { getFeaturesByVertical } from "@/lib/marketing/features-data";

/**
 * Two-row infinite marquee for the Nightlife page.
 *   Row 1 (L→R): every Nightlife feature name (the lineup)
 *   Row 2 (R→L): venue types Redy is built for (the audience)
 *
 * Lives between the Stack and Cta sections as a continuity ribbon.
 * Same pattern as the home FeatureMarquee but violet-themed for nightlife.
 */
export default function NightlifeMarquee() {
  const { t } = useLang();

  const features = getFeaturesByVertical("nightlife");
  const featureNames = features.map((f) => t(f.card.title.en, f.card.title.es));

  // The venues Redy Nightlife fits.
  const venueTypes = [
    t("Nightclub", "Nightclub"),
    t("Bar", "Bar"),
    t("Lounge", "Lounge"),
    t("Live music venue", "Venue de música en vivo"),
    t("Cocktail bar", "Bar de cocteles"),
    t("Wedding venue", "Salón de bodas"),
    t("Sports bar", "Bar deportivo"),
    t("NYE event", "Evento de fin de año"),
    t("Pop-up venue", "Pop-up"),
    t("Beach club", "Beach club"),
    t("Rooftop bar", "Rooftop bar"),
    t("Comedy club", "Club de comedia"),
  ];

  return (
    <section className="relative py-20 bg-[#0F0B1F] overflow-hidden border-y border-white/5">
      {/* Subtle violet wash */}
      <div className="absolute top-0 left-1/3 w-[500px] h-[200px] bg-[#7C3AED]/12 rounded-full blur-[120px] pointer-events-none" />

      {/* Section eyebrow */}
      <div className="relative text-center mb-8 px-6">
        <div className="text-[11px] font-bold tracking-widest uppercase text-[#F472B6]/70">
          {t("Built for every kind of venue", "Hecho para todo tipo de venue")}
        </div>
      </div>

      {/* Row 1 — features L→R */}
      <MarqueeTrack
        items={featureNames}
        direction="left"
        textClass="text-white"
        bulletClass="bg-[#F472B6]"
        speed={55}
      />

      {/* Row 2 — venue types R→L */}
      <div className="mt-5">
        <MarqueeTrack
          items={venueTypes}
          direction="right"
          textClass="text-white/45"
          bulletClass="bg-[#C4B5FD]/40"
          speed={48}
          smaller
        />
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MarqueeTrack
// ─────────────────────────────────────────────────────────────────────────────

interface MarqueeTrackProps {
  items: string[];
  direction: "left" | "right";
  textClass: string;
  bulletClass: string;
  speed: number;
  smaller?: boolean;
}

function MarqueeTrack({ items, direction, textClass, bulletClass, speed, smaller }: MarqueeTrackProps) {
  const animationName =
    direction === "left" ? "nl-marquee-left" : "nl-marquee-right";

  return (
    <div
      className="relative overflow-hidden"
      style={{
        maskImage:
          "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
      }}
    >
      <div
        className="flex w-max gap-10 items-center hover:[animation-play-state:paused]"
        style={{ animation: `${animationName} ${speed}s linear infinite` }}
      >
        {Array.from({ length: 4 }).flatMap((_, set) =>
          items.map((label, i) => (
            <div
              key={`${set}-${i}`}
              className={`shrink-0 flex items-center gap-6 ${textClass} ${
                smaller ? "text-[15px]" : "text-[22px] md:text-[28px]"
              } font-black tracking-tight`}
            >
              <span
                className={`shrink-0 rounded-full ${bulletClass} ${
                  smaller ? "w-1.5 h-1.5" : "w-2 h-2"
                }`}
              />
              <span className="whitespace-nowrap">{label}</span>
            </div>
          ))
        )}
      </div>

      <style jsx>{`
        @keyframes nl-marquee-left {
          from { transform: translateX(0); }
          to   { transform: translateX(-25%); }
        }
        @keyframes nl-marquee-right {
          from { transform: translateX(-25%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
